// Weekly-plan data-shape helpers. Ported as-is from WEEKLY_2.HTM.
import { DAYS, MEAL_SLOTS, PLANNABLE_DISH_TYPES, DINING_OUT } from "./constants";
import { uid } from "./utils";

export function defaultPlanDefaults() {
  return {
    defaultServings: 4,
    weekStartDay: "Monday",
    mealTimeBudgets: {},
    householdMembers: [],
  };
}

// `userId` links the person to an account (see 0038): null for an unlinked
// diner managed by every household member, or the signed-in user's id for a
// diner whose targets only that account can see. `targetsHidden` is true for
// a diner linked to somebody else -- the client only ever holds their name.
export function emptyHouseholdMember(userId = null) {
  return { id: uid(), name: "", userId, targetsHidden: false, dailyCalorieTarget: 0, dailyProteinTarget: 0, dailyCarbsTarget: 0, dailyFatTarget: 0 };
}

// Restoring a backup replaces the household's people with the file's -- but
// a backup must never rewire who owns whose numbers. Rows the viewer can't
// see (linked to someone else) are kept exactly as they are; a restored row
// that matches a current one keeps that row's account link; anything else
// comes back unlinked. Hidden rows in the file are ignored -- they carry no
// numbers.
export function mergeImportedMembers(current, imported) {
  const currentById = new Map((current || []).map((m) => [m.id, m]));
  const restored = (imported || [])
    .filter((m) => m && !m.targetsHidden)
    .filter((m) => !currentById.get(m.id)?.targetsHidden)
    .map((m) => ({ ...emptyHouseholdMember(), ...m, userId: currentById.get(m.id)?.userId ?? null, targetsHidden: false }));
  return [...(current || []).filter((m) => m.targetsHidden), ...restored];
}

export function dayBudgetFromDefaults(defaults, day) {
  const meals = defaults.mealTimeBudgets?.[day] || {};
  return MEAL_SLOTS.reduce((sum, slot) => sum + (Number(meals[slot]) || 0), 0);
}

export function buildDefaultWeek(defaults) {
  const dayBudgets = {};
  DAYS.forEach((day) => {
    const sum = dayBudgetFromDefaults(defaults, day);
    if (sum > 0) dayBudgets[day] = sum;
  });
  return {
    title: "",
    days: {},
    snacks: [],
    preserves: [],
    medicinal: [],
    servings: defaults.defaultServings || 4,
    dayBudgets,
  };
}

// A day/slot's value should always be an object keyed by dish type
// (e.g. { entree: "recipeId" }). This guards against any legacy or
// malformed data (e.g. a bare recipe id string from an older format)
// so the planner never crashes trying to read dish types off it.
export function normalizeDishes(value) {
  if (!value) return {};
  if (typeof value === "string") return { entree: value };
  if (typeof value === "object" && !Array.isArray(value)) {
    // Only keep recognized dish-type keys — this also self-heals any data
    // that was previously corrupted with stray keys (e.g. numeric indices
    // left over from spreading a raw string into an object). Check key
    // presence, not value truthiness, so an empty-string placeholder (a
    // dish row that's showing but doesn't have a recipe picked yet) is
    // preserved instead of being silently dropped.
    const clean = {};
    PLANNABLE_DISH_TYPES.forEach((dt) => {
      if (dt in value) clean[dt] = value[dt];
    });
    return clean;
  }
  return {};
}

// How each recipe is used across every loaded week: recipeId -> { slots, weeks }
// (slots = number of plan placements, weeks = number of distinct weeks). Covers
// meals and the snack/preserves/medicinal lists. Used by the leave/remove
// flows to say how much of the household's plan depends on someone's recipes.
export function countPlanUsage(plansByWeek) {
  const usage = new Map();
  const bump = (recipeId, weekKey) => {
    if (!recipeId || recipeId === DINING_OUT) return;
    const entry = usage.get(recipeId) || { slots: 0, weekKeys: new Set() };
    entry.slots += 1;
    entry.weekKeys.add(weekKey);
    usage.set(recipeId, entry);
  };
  Object.entries(plansByWeek || {}).forEach(([weekKey, week]) => {
    Object.values(week?.days || {}).forEach((slots) => {
      Object.values(slots || {}).forEach((dishes) => {
        Object.values(normalizeDishes(dishes)).forEach((id) => bump(id, weekKey));
      });
    });
    ["snacks", "preserves", "medicinal"].forEach((list) => (week?.[list] || []).forEach((entry) => bump(entry.recipeId, weekKey)));
  });
  return new Map([...usage].map(([id, { slots, weekKeys }]) => [id, { slots, weeks: weekKeys.size }]));
}

export function weekMealCount(week) {
  if (!week) return 0;
  let n = 0;
  Object.values(week.days || {}).forEach((slots) => {
    Object.values(slots || {}).forEach((dishes) => {
      n += Object.values(normalizeDishes(dishes)).filter(Boolean).length;
    });
  });
  n += (week.snacks || []).length;
  n += (week.preserves || []).length;
  n += (week.medicinal || []).length;
  return n;
}

// Active + cook minutes for one day of a week's plan. A placement marked as
// leftovers is eaten, not cooked again, so it adds no time (feedback #3).
// "Dining out" and missing recipes add nothing either -- recipeById returns
// nothing for them.
export function dayCookMinutes(week, day, recipeById) {
  let total = 0;
  Object.entries(week?.days?.[day] || {}).forEach(([slot, dishes]) => {
    Object.entries(normalizeDishes(dishes)).forEach(([dishType, id]) => {
      if (!id || week.leftovers?.[`${day}|${slot}|${dishType}`]) return;
      const r = recipeById(id);
      if (r) total += (r.activeTime || 0) + (r.cookTime || 0);
    });
  });
  return total;
}

// The whole week: every day's meals, plus the snacks, preserves and medicinal
// lists (those can't be marked as leftovers).
export function weekCookMinutes(week, recipeById) {
  let total = DAYS.reduce((sum, day) => sum + dayCookMinutes(week, day, recipeById), 0);
  ["snacks", "preserves", "medicinal"].forEach((list) =>
    (week?.[list] || []).forEach((entry) => {
      const r = recipeById(entry.recipeId);
      if (r) total += (r.activeTime || 0) + (r.cookTime || 0);
    })
  );
  return total;
}
